import { useRef, useCallback } from 'react';
import { fireErrorAnalytics } from '@jira/platform__error-reporting/src/fireErrorAnalytics.tsx';
import { usePublish } from '@atlaskit/rovo-triggers';
import { setMark } from '@atlassian/jira-common-performance/src/marks.tsx';
import { performPostRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import {
	prefetchApiReadyMark,
	prefetchApiStartMark,
} from '@atlassian/jira-providers-spa-apdex-analytics/src/marks.tsx';
import {
	PACKAGE_NAME,
	ROVO_TRIAL_GUIDE_AGENT_CONFIG,
	ROVO_AGENT_CONFIG_ID,
	ROVO_DEFAULT_AGENT_CONFIG,
	TEAM_NAME,
	type TrialAssistantSource,
} from '../../common/constants.tsx';

export const ensureTrialAssistantProvisioned = async (
	cloudId: string,
	source: TrialAssistantSource,
) => {
	try {
		setMark(prefetchApiStartMark);
		await performPostRequest(
			`/gateway/api/assist/v1/agent/provisioning/ootb/${ROVO_AGENT_CONFIG_ID}`,
			{
				headers: {
					'X-Product': 'jira',
					'X-Experience-Id': 'ai-mate',
					'Atl-Cloudid': cloudId,
				},
			},
		);
		setMark(prefetchApiReadyMark);

		return true;
	} catch (error: unknown) {
		fireErrorAnalytics({
			error:
				error instanceof Error
					? error
					: new Error(
							`Failed to provision jira trial asisstant Rovo agent: ${JSON.stringify(error)}`,
						),
			meta: {
				id: source,
				teamName: TEAM_NAME,
				packageName: PACKAGE_NAME,
			},
		});
		return false;
	}
};

export const useOpenTrialAssistantAgent = (source: TrialAssistantSource) => {
	const cloudId = useCloudId();
	const publish = usePublish('ai-mate');
	const stableSource = useRef(source);

	return useCallback(async () => {
		const result = await ensureTrialAssistantProvisioned(cloudId, stableSource.current);
		if (result) {
			publish({
				type: 'chat-new',
				source: stableSource.current,
				product: 'jira',
				data: ROVO_TRIAL_GUIDE_AGENT_CONFIG,
			});
		} else {
			publish({
				type: 'chat-new',
				source: stableSource.current,
				product: 'jira',
				data: ROVO_DEFAULT_AGENT_CONFIG,
			});
		}
	}, [cloudId, publish]);
};
